"use client";

import Link from "next/link";
import { localizePath } from "@repo/lib";
import { useI18n } from "@repo/ui/providers";
import { useState, useMemo } from "react";
import type { ChangeEvent } from "react";
import {
  faqEntries,
  allLabels,
  type FAQLabel,
} from "@/games/thgl-web/lib/faq-entries";
import { FaqLabelBadge } from "@/games/thgl-web/components/faq-label-badge";

// FAQ headlines/questions/answers are content and stay English; the chrome
// follows the page locale.
export function FAQList() {
  const { t, locale } = useI18n();
  const [selectedLabels, setSelectedLabels] = useState<FAQLabel[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredEntries = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return faqEntries.filter((entry) => {
      const matchesLabels =
        selectedLabels.length === 0 ||
        entry.labels.some((label) => selectedLabels.includes(label));

      if (!matchesLabels) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      return (
        entry.headline.toLowerCase().includes(normalizedQuery) ||
        entry.question.toLowerCase().includes(normalizedQuery) ||
        entry.answer.toLowerCase().includes(normalizedQuery)
      );
    });
  }, [searchQuery, selectedLabels]);

  const toggleLabel = (label: FAQLabel) => {
    setSelectedLabels((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    );
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const clearFilters = () => {
    setSelectedLabels([]);
    setSearchQuery("");
  };

  const hasActiveFilters =
    selectedLabels.length > 0 || searchQuery.trim().length > 0;

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="relative">
          <input
            type="search"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={t("www.faq.searchPlaceholder")}
            aria-label={t("www.faq.searchAria")}
            className="w-full rounded-md border border-muted-foreground/30 bg-background px-3 py-2 text-sm shadow-xs outline-hidden ring-brand/20 focus:border-brand focus:ring-2"
          />
        </div>

        {allLabels.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {allLabels.map((label) => {
              const isSelected = selectedLabels.includes(label);
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => toggleLabel(label)}
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    isSelected
                      ? "border-brand/60 bg-brand/10 text-brand"
                      : "border-muted-foreground/20 text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground"
                  }`}
                >
                  <FaqLabelBadge
                    label={label}
                    variant="minimal"
                    dotClassName="h-2.5 w-2.5"
                    t={t}
                  />
                </button>
              );
            })}
          </div>
        )}

        {hasActiveFilters && (
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>
              {t("www.faq.showing", {
                vars: {
                  shown: String(filteredEntries.length),
                  total: String(faqEntries.length),
                },
              })}
            </span>
            <button
              type="button"
              onClick={clearFilters}
              className="font-medium text-brand hover:underline"
            >
              {t("www.faq.clearFilters")}
            </button>
          </div>
        )}
      </div>

      {filteredEntries.length === 0 ? (
        <div className="rounded-lg border border-dashed border-muted-foreground/30 py-12 text-center text-sm text-muted-foreground">
          <p>{t("www.faq.noMatches")}</p>
          <button
            type="button"
            onClick={clearFilters}
            className="mt-4 text-brand hover:underline"
          >
            {t("www.faq.clearFilters")}
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredEntries.map((faq) => (
            <div
              key={faq.id}
              className="rounded-lg border border-muted-foreground/20 bg-background/60 p-4 shadow-xs transition-colors hover:border-muted-foreground/40"
            >
              <div className="space-y-3">
                <Link
                  href={localizePath(`/faq/${faq.id}`, locale)}
                  className="block transition-colors hover:text-foreground"
                >
                  <h2 className="text-lg font-semibold text-foreground">
                    {faq.headline}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {faq.question}
                  </p>
                </Link>

                <div className="flex flex-wrap gap-2">
                  {faq.labels.map((label) => (
                    <FaqLabelBadge key={label} label={label} t={t} />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
